import { motion } from 'framer-motion';
import { Camera, ExternalLink, Mic, Plug } from 'lucide-react';

const ROLEMODEL_URL = 'https://rolemodel.ai';

const HIGHLIGHTS = [
  {
    icon: Camera,
    title: 'From your photo',
    text: 'Upload a portrait and launch a photorealistic talking avatar with your voice, personality, and knowledge.',
  },
  {
    icon: Mic,
    title: 'Talk face-to-face',
    text: 'Live video conversations in the browser — the same kind of experience powering living websites like this one. Daily intelligence, memory, and goals help Concierge stay useful between sessions.',
  },
  {
    icon: Plug,
    title: 'Tools & connections',
    text: 'Ask your avatar to work with the apps you use — connect Notion and LinkedIn, control home devices, and (on Pro) vibe code with Cursor via Concierge MCP. More connectors are shipping continuously.',
  },
];

export default function ConciergeLandingPage() {
  const openRoleModel = () => window.open(ROLEMODEL_URL, '_blank', 'noopener,noreferrer');

  return (
    <div className="min-h-screen bg-black text-white">
      <section className="relative overflow-hidden px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 60%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <div className="relative mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
              Concierge 2.3 by RoleModel.AI
            </span>

            <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">Role Model AI</h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
              Role Model AI is SELF Labs&apos; platform for photorealistic talking avatars. Concierge
              2.3 turns a single portrait into a face-to-face AI you can talk to in the browser —
              with tools and connections so your avatar can work alongside you (Notion, LinkedIn,
              home devices, and vibe coding in Cursor) — or embed on your own site.
            </p>

            <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
              Free to start · Pro from $20/mo · Expert embed from $99/mo
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <motion.button
                type="button"
                onClick={openRoleModel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90"
              >
                Try Concierge 2.3
                <ExternalLink className="h-4 w-4" />
              </motion.button>
              <motion.button
                type="button"
                onClick={openRoleModel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/35 hover:bg-white/10"
              >
                Learn more on rolemodel.ai
              </motion.button>
            </div>

            <p className="mx-auto mt-6 max-w-xl text-xs leading-relaxed text-white/40">
              Put your avatar on your site as a bubble, fullscreen launcher, or iframe with Concierge
              Expert.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
          {HIGHLIGHTS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.4 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
            >
              <item.icon className="mb-4 h-7 w-7 text-white/70" />
              <h2 className="font-semibold">{item.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 px-4 py-20 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center sm:p-10"
        >
          <p className="text-white/65">
            Pricing, feature comparison, live demos, and signup live on the Role Model AI site — not
            here. Visit <span className="text-white/80">rolemodel.ai</span> for Concierge 2.3 —
            talking avatars, Daily Brief, Tools, and vibe coding with your agent.
          </p>
          <motion.button
            type="button"
            onClick={openRoleModel}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
          >
            Go to rolemodel.ai
            <ExternalLink className="h-4 w-4" />
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
}
